import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalizedRoutes } from './useLocalizedRoutes';
import { useTranslation } from 'react-i18next';
import { getEquivalentRoute } from '../utils/routeLocalization';

interface NavigationOptions {
  replace?: boolean;
  state?: any;
}

interface UseLocalizedNavigationReturn {
  navigateToRoute: (routeKey: keyof ReturnType<typeof useLocalizedRoutes>['routes'], options?: NavigationOptions) => void;
  navigateWithLang: (path: string, options?: NavigationOptions) => void;
  switchLanguageAndNavigate: (newLanguage: string) => void;
  getCurrentRouteKey: () => string | null;
  currentLanguage: string;
}

/**
 * Custom hook for language-aware navigation
 * Provides navigation methods that automatically use localized routes
 */
export const useLocalizedNavigation = (): UseLocalizedNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getLocalizedPath, currentLanguage } = useLocalizedRoutes();
  const { i18n } = useTranslation();

  /**
   * Navigate to a localized route
   * @param routeKey - The key of the route from useLocalizedRoutes
   * @param options - Additional navigation options
   */
  const navigateToRoute = (
    routeKey: keyof ReturnType<typeof useLocalizedRoutes>['routes'], 
    options: NavigationOptions = {}
  ): void => {
    const path = getLocalizedPath(routeKey);
    const searchParams = new URLSearchParams(location.search);
    
    // Preserve existing query params and add/update lang param
    searchParams.set('lang', currentLanguage);
    
    const fullPath = `${path}?${searchParams.toString()}`;
    navigate(fullPath, options);
  };

  /**
   * Navigate to a custom path with language parameter
   * @param path - The path to navigate to
   * @param options - Additional navigation options
   */
  const navigateWithLang = (path: string, options: NavigationOptions = {}): void => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', currentLanguage);
    
    const fullPath = `${path}?${searchParams.toString()}`;
    navigate(fullPath, options);
  };

  /**
   * Switch language and navigate to equivalent page.
   *
   * SCROLL PRESERVATION
   * -------------------
   * Previously this method always called `navigate(newPath, { replace: true })`,
   * which — even with `replace` — triggers React Router's route match. Any
   * scrollable container in the page tree that re-renders as part of that
   * match ends up with a fresh scrollTop of 0, so switching language while
   * scrolled mid-page snapped the user back to the top.
   *
   * The fix branches on whether the pathname actually changes:
   *   • Same-path (the common case — this app uses `?lang=X` as a query
   *     param, not a path segment, so pathname rarely changes). Update
   *     the URL in-place via `window.history.replaceState`. React Router
   *     doesn't re-run its route match for a raw history-state update,
   *     so nothing remounts and scroll is preserved natively by the
   *     browser. i18n language change alone will re-render subscribed
   *     components, which is exactly what we want.
   *   • Cross-path (rare — localized route segments like `/en/dashboard`
   *     → `/sv/instrumentpanel`). We still need React Router navigation
   *     to swap the route component, so we capture scroll offsets first
   *     and restore them across two rAFs (the second frame gives the
   *     new route's layout time to establish before we scroll — one rAF
   *     isn't enough on mount-heavy routes).
   *
   * @param newLanguage - The new language code
   */
  const switchLanguageAndNavigate = (newLanguage: string): void => {
    // Change the i18n language
    i18n.changeLanguage(newLanguage);

    // Store in localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('i18nextLng', newLanguage);
    }

    // Get current path and find equivalent in new language
    const currentPath = location.pathname;
    const equivalentPath = getEquivalentRoute(currentPath, currentLanguage, newLanguage);

    // Create new search params with updated language
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', newLanguage);
    const newSearch = searchParams.toString();
    const newPath = `${equivalentPath}${newSearch ? `?${newSearch}` : ''}`;

    // ---- Same-path fast path (99% of language switches) --------------------
    // Just update the URL — no route match, no remount, scroll preserved.
    if (equivalentPath === currentPath && typeof window !== 'undefined') {
      window.history.replaceState(window.history.state, '', newPath);
      return;
    }

    // ---- Cross-path fallback (localized route segments) --------------------
    // Capture scroll (window + document) BEFORE navigation, then restore
    // once the new route has laid out. Two rAFs handles the common case
    // where the new route mounts async / with an entrance skeleton.
    const savedX = typeof window !== 'undefined' ? window.scrollX : 0;
    const savedY = typeof window !== 'undefined' ? window.scrollY : 0;

    navigate(newPath, { replace: true });

    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedY, left: savedX, behavior: 'auto' });
        });
      });
    }
  };

  /**
   * Get the current route key based on current pathname
   * @returns The route key or null if not found
   */
  const getCurrentRouteKey = (): string | null => {
    const { routes } = useLocalizedRoutes();
    const currentPath = location.pathname;
    
    for (const [key, path] of Object.entries(routes)) {
      if (path === currentPath) {
        return key;
      }
    }
    return null;
  };

  return {
    navigateToRoute,
    navigateWithLang,
    switchLanguageAndNavigate,
    getCurrentRouteKey,
    currentLanguage
  };
};
